import { MyGrievances } from "./Relations";
import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  PROFILE_FIELDS, cancelProfileChange, loadEducationLevels, loadLeaveRequests, loadLeaveTypes, loadLetterRequests, loadLetterTemplates,
  loadMyDetails, loadProfileChanges, requestProfileChange,
  type LeaveRequest, type LetterRequest, type ProfileChange, type ProfileDetails, type ProfileField,
} from "../data";
import { ProfileChangeView, fieldLabel } from "../components/ProfileChangeView";
import { MyLoans } from "../components/MyLoans";
import { MyExpenses } from "./Expenses";
import { MyAssets } from "./Assets";
import { MyResignation } from "./Offboarding";
import { MyOnboarding } from "./Onboarding";
import { MyPerformance } from "./Performance";
import { MyTraining } from "./Training";
import type { Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const statusTone = (s: string) => (s === "approved" || s === "issued" ? "success" : s === "rejected" || s === "declined" ? "danger" : s === "pending" ? "warning" : "neutral");

/** Self-service hub: update my details (HR approves) and follow everything I have asked for. */
export function MyRequests({ ws, onGo }: { ws: Workspace; onGo: (page: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const emp = ws.membership.employment_id;
  const [details, setDetails] = useState<ProfileDetails | null>(null);
  const [changes, setChanges] = useState<ProfileChange[]>([]);
  const [leave, setLeave] = useState<LeaveRequest[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<Map<string, { name_en: string; name_ar: string | null }>>(new Map());
  const [letters, setLetters] = useState<LetterRequest[]>([]);
  const [templates, setTemplates] = useState<Map<string, { name_en: string; name_ar: string | null }>>(new Map());
  const [edu, setEdu] = useState<{ code: string; name_en: string; name_ar: string }[]>([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!emp) return;
    try {
      const [d, c] = await Promise.all([loadMyDetails(emp), loadProfileChanges(cid, emp)]);
      setDetails(d);
      setChanges(c);
      if (ws.enabled.has("leave")) setLeave(await loadLeaveRequests(cid, emp));
      if (ws.enabled.has("letters")) setLetters(await loadLetterRequests(cid, emp));
    } catch (e) { setError(errText(e, t("errorGeneric"))); }
  };
  useEffect(() => {
    void refresh();
    void loadEducationLevels().then(setEdu);
    if (ws.enabled.has("leave")) void loadLeaveTypes(cid).then((x) => setLeaveTypes(new Map(x.map((y) => [y.id, y]))));
    if (ws.enabled.has("letters")) void loadLetterTemplates(cid).then((x) => setTemplates(new Map(x.map((y) => [y.id, y]))));
  }, [cid, emp]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!emp) return <div className="page"><p className="alert info">{t("linkFirst")}</p></div>;
  const pending = changes.find((c) => c.status === "pending");
  const show = (k: ProfileField, v: string | null) => {
    if (!v) return <span className="muted">—</span>;
    if (k === "marital_status") return t(`marital_${v}` as StringKey);
    if (k === "education_level") { const e = edu.find((x) => x.code === v); return e ? pick(e.name_en, e.name_ar) : v; }
    return <span dir="auto">{v}</span>;
  };

  return (
    <div className="page">
      {error && <p className="alert danger" role="alert">{error}</p>}
      <section className="card">
        <div className="row spread">
          <h2>{t("myDetails")}</h2>
          {!editing && !pending && <button type="button" className="btn secondary small" onClick={() => setEditing(true)}>{t("requestChange")}</button>}
        </div>
        {pending && (
          <div className="alert info">
            <strong>{t("changeWaiting")}</strong>
            <ProfileChangeView change={pending} edu={edu} />
            <button type="button" className="btn secondary small" onClick={async () => {
              try { await cancelProfileChange(pending.id); await refresh(); } catch (e) { setError(errText(e, t("errorGeneric"))); }
            }}>{t("cancelRequest")}</button>
          </div>
        )}
        {editing && details ? (
          <DetailsForm ws={ws} details={details} edu={edu} onDone={async (sent) => { setEditing(false); if (sent) await refresh(); }} />
        ) : details && (
          <dl className="kv-list">
            {PROFILE_FIELDS.map((k) => <div key={k} className="kv"><dt>{t(fieldLabel[k])}</dt><dd>{show(k, details[k])}</dd></div>)}
          </dl>
        )}
        <p className="muted small-note">{t("detailsHelp")}</p>
      </section>

      {ws.enabled.has("onboarding") && <MyOnboarding ws={ws} />}
      {ws.enabled.has("performance") && <MyPerformance ws={ws} />}
      {ws.enabled.has("training") && <MyTraining ws={ws} />}
      {ws.enabled.has("expenses") && <MyExpenses ws={ws} />}
      {ws.enabled.has("loans") && <MyLoans ws={ws} />}
      {ws.enabled.has("assets") && <MyAssets ws={ws} />}

      <section className="card">
        <h2>{t("myRequestsAll")}</h2>
        <ul className="req-list">
          {leave.slice(0, 15).map((r) => {
            const lt = leaveTypes.get(r.leave_type_id);
            return (
              <li key={r.id}>
                <span><strong>{lt ? pick(lt.name_en, lt.name_ar) : t("navLeave")}</strong> · <span className="mono">{r.start_date}{r.end_date !== r.start_date ? ` → ${r.end_date}` : ""}</span></span>
                <span className={`badge ${statusTone(r.status)}`}>{t(`leaveStatus_${r.status}` as StringKey)}</span>
              </li>
            );
          })}
          {letters.map((r) => {
            const x = templates.get(r.template_id);
            return (
              <li key={r.id}>
                <span><strong>{x ? pick(x.name_en, x.name_ar) : t("navLetters")}</strong>{r.purpose ? ` · ${r.purpose}` : ""} · <span className="mono">{r.created_at.slice(0, 10)}</span></span>
                <span className={`badge ${statusTone(r.status)}`}>{t(`letterStatus_${r.status}` as StringKey)}</span>
              </li>
            );
          })}
          {changes.filter((c) => c.status !== "pending").map((c) => (
            <li key={c.id}>
              <span><strong>{t("detailsChange")}</strong> · {Object.keys(c.changes).map((k) => t(fieldLabel[k as ProfileField])).join(", ")} · <span className="mono">{c.created_at.slice(0, 10)}</span>
                {c.decision_note && <span className="muted small-note"> “{c.decision_note}”</span>}</span>
              <span className={`badge ${statusTone(c.status)}`}>{t(`leaveStatus_${c.status}` as StringKey)}</span>
            </li>
          ))}
        </ul>
        {leave.length + letters.length + changes.length === 0 && <p className="muted">{t("noRequestsYet")}</p>}
        <div className="row">
          {ws.enabled.has("leave") && <button type="button" className="btn secondary small" onClick={() => onGo("myleave")}>{t("navMyLeave")}</button>}
          {ws.enabled.has("letters") && <button type="button" className="btn secondary small" onClick={() => onGo("myletters")}>{t("navMyLetters")}</button>}
        </div>
      </section>
      {ws.enabled.has("employee_relations") && <MyGrievances ws={ws} />}
      {ws.enabled.has("offboarding") && <MyResignation ws={ws} />}
    </div>
  );
}

function DetailsForm({ ws, details, edu, onDone }: {
  ws: Workspace; details: ProfileDetails; edu: { code: string; name_en: string; name_ar: string }[]; onDone: (sent: boolean) => void;
}) {
  const { t, pick } = useI18n();
  const [f, setF] = useState<ProfileDetails>(details);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = PROFILE_FIELDS.filter((k) => (f[k] ?? "") !== (details[k] ?? ""));

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestProfileChange(ws.membership.company_id, Object.fromEntries(changed.map((k) => [k, f[k] ?? ""])), note);
      onDone(true);
    } catch (err) { setError(errText(err, t("errorGeneric"))); }
    finally { setBusy(false); }
  }
  const input = (k: ProfileField, type = "text") => (
    <label className="field"><span>{t(fieldLabel[k])}</span>
      <input type={type} dir="auto" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>
  );
  return (
    <form className="stack" onSubmit={send}>
      <div className="field-row">{input("mobile", "tel")}{input("personal_email", "email")}</div>
      {input("address")}
      <div className="field-row">{input("emergency_contact_name")}{input("emergency_contact_phone", "tel")}</div>
      <div className="field-row">
        <label className="field"><span>{t("pfMarital")}</span>
          <select value={f.marital_status ?? ""} onChange={(e) => setF({ ...f, marital_status: e.target.value || null })}>
            <option value="">—</option>
            {["single", "married", "divorced", "widowed"].map((m) => <option key={m} value={m}>{t(`marital_${m}` as StringKey)}</option>)}
          </select></label>
        <label className="field"><span>{t("pfEducation")}</span>
          <select value={f.education_level ?? ""} onChange={(e) => setF({ ...f, education_level: e.target.value || null })}>
            <option value="">—</option>
            {edu.map((x) => <option key={x.code} value={x.code}>{pick(x.name_en, x.name_ar)}</option>)}
          </select></label>
      </div>
      <label className="field"><span>{t("noteForHr")}</span><input value={note} onChange={(e) => setNote(e.target.value)} /></label>
      {error && <p className="alert danger" role="alert">{error}</p>}
      <div className="actions">
        <button type="button" className="btn secondary" onClick={() => onDone(false)}>{t("cancel")}</button>
        <button className="btn primary" disabled={busy || changed.length === 0}>{t("sendToHr")}</button>
      </div>
    </form>
  );
}
